import { AD_ACCOUNT_STATUS } from '@/consts.js';

export default {
  methods: {
    brandStatusText(brand) {
      if (!brand) {
        return null;
      }

      if (brand.deleted_at) {
        return {
          type: 'error',
          label: this.tr('Brand removed'),
          text: this.tr('The ":brandName" brand has been removed from :app.', {
            ':brandName': brand.name,
          }),
        };
      }

      if (brand.is_active === false) {
        const brandPath = '/brands/' + brand.id;
        const isOnBrandPage = this.$route?.path?.startsWith(brandPath) ?? false;

        return {
          type: 'info',
          label: this.tr('brand.inactive'),
          text: this.tr('The ":brandName" brand is inactive. Its automations do not run until you reactivate it.', {
            ':brandName': brand.name,
          }),
          link: isOnBrandPage ? null : brandPath,
          linkLabel: isOnBrandPage ? null : this.tr('Go to brand'),
        };
      }

      if (brand.token_expired) {
        if (Array.isArray(brand.blocked_routes) && brand.blocked_routes.length !== 0) {
          return this.blockedRoutesStatus(brand.blocked_routes);
        }

        return this.expiredConnectionStatus(brand.related_connected_profile);
      }

      return null;
    },

    // The brand's assets are only reached through profiles that cannot be used right now. Each of
    // those people can unblock it, and each has a different thing to do, so they are listed one by one.
    blockedRoutesStatus(routes) {
      const items = routes.map(route => {
        const profile = route.connected_profile;
        const profileName = escapeHtml(profile?.name ?? '');
        const who = route.owner_name
          ? this.tr(':person (:profileType: :profileName)', {
            ':person': escapeHtml(route.owner_name),
            ':profileType': connectedProfileTypeLabel(this, profile),
            ':profileName': profileName,
          })
          : profileName;

        return '<li><b>' + who + '</b>: ' + blockedRouteAction(this, route.reason) + '</li>';
      });

      return {
        type: 'error',
        label: this.tr('Profile connection needs attention'),
        text: this.tr('The assets of this brand can currently only be reached through the profiles below, and none of them can be used right now. Until one of them is fixed, the automations using the brand do not run.'),
        whatToDo: '<ul>' + items.join('') + '</ul>',
        link: integrationsPathForPlatform(routes[0]?.connected_profile?.platform),
        linkLabel: this.tr('Go to Connected Profiles'),
      };
    },

    expiredConnectionStatus(connectedProfile) {
      const profileName = connectedProfile?.name;
      const profileType = connectedProfileTypeLabel(this, connectedProfile);

      return {
        type: 'error',
        label: this.tr('Expired profile connection'),
        text: profileName
          ? this.tr('The connection of the ":profileName" :profileType has expired, so the assets it connected cannot be used.', {
            ':profileName': profileName,
            ':profileType': profileType,
          })
          : this.tr('The connection of the profile behind this asset has expired, so the asset cannot be used.'),
        whatToDo: this.tr('Reconnect the :profileType. Until then the automations using it do not run.', {
          ':profileType': profileType,
        }),
        link: integrationsPathForPlatform(connectedProfile?.platform),
        linkLabel: this.tr('Reconnect'),
      };
    },

    expiredConnectionReason(connectedProfile) {
      const profileName = connectedProfile?.name;
      if (!profileName) {
        return this.tr('The connection of the profile behind this automation has expired.');
      }

      return this.tr('The connection of the ":profileName" profile has expired. Reconnect it to start the automation again.', {
        ':profileName': profileName,
      });
    },

    adAccountStatusLabel(adAccount) {
      if (!adAccount) {
        return null;
      }

      const list = this.$store.getters['app/facebookAdAccountStatuses'] || [];
      const row = list.find(i => i.machine_name === adAccount.status);
      return row ? this.tr(row.label) : null;
    },

    adAccountStatusIsUnsettled(adAccount) {
      return adAccount?.status === AD_ACCOUNT_STATUS.UNSETTLED
        || adAccount?.status === AD_ACCOUNT_STATUS.PENDING_SETTLEMENT;
    },

    adAccountStatusIsPermissionError(adAccount) {
      return adAccount?.status === AD_ACCOUNT_STATUS.PERMISSION_ERROR;
    },

    adAccountStatusIsWithoutPaymentMethod(adAccount) {
      return adAccount?.has_payment_method === false;
    },

    adAccountStatusText(adAccount, isAiMode = false) {
      if (!adAccount) {
        return null;
      }

      let adAccountType = this.tr('Ad Account');
      switch (adAccount.platform) {
        case 'facebook':
          adAccountType = this.tr('Meta Ad Account');
          break;
      }

      let profileType = this.tr('Profile');
      switch (adAccount.platform) {
        case 'facebook':
          profileType = this.tr('Facebook Profile');
          break;
      }

      if (adAccount.deleted_at) {
        return {
          type: 'error',
          label: this.tr('Ad account disconnected'),
          text: this.tr(
            'The ":adAccountName" :adAccountType associated with the brand has been disconnected from :app.',
            {
              ':adAccountName': adAccount.name,
              ':adAccountType': adAccountType,
            },
          ),
          whatToDo: this.tr(
            'Reconnect the profile that links the :adAccountType to :app.',
            {
              ':adAccountType': adAccountType,
            },
          ),
        };
      }

      if (adAccount.lost_access) {
        return {
          type: 'error',
          label: this.tr('Lost access to ad account'),
          text: this.tr(
            'Access to the ":adAccountName" :adAccountType connected by the :profileType has been terminated.',
            {
              ':adAccountName': adAccount.name,
              ':adAccountType': adAccountType,
              ':profileType': profileType,
            },
          ),
          whatToDo: this.tr(
            'Please ask the authorized person to restore access or reconnect the Ad Account through a :profileType that still has access.',
            {
              ':profileType': profileType,
            },
          ),
        };
      }

      if (this.adAccountStatusIsUnsettled(adAccount)) {
        const metaLink = 'https://business.facebook.com/billing_hub/payment_activity?asset_id=' + facebookAdAccountExternalId(adAccount);
        return {
          type: 'error',
          label: this.tr('Unsettled ad account'),
          text: this.tr('This ad account has an unsettled debt at Meta and cannot be used until the debt is settled.'),
          whatToDo: this.tr('You can view and settle your debt at Meta via the following link:')
            + '<br /><a href="' + metaLink + '" target="_blank">' + metaLink + '</a>',
          retry: {
            label: this.tr('Paid, let\'s try again'),
            errorMessage: this.tr('The advertisement account still shows a payment debt. Please try again.'),
            isStillFailing: updated => updated.status !== AD_ACCOUNT_STATUS.ACTIVE,
          },
        };
      }

      if (this.adAccountStatusIsPermissionError(adAccount)) {
        const metaLink = 'https://business.facebook.com/settings/ad-accounts/' + facebookAdAccountExternalId(adAccount);
        return {
          type: 'error',
          label: this.tr('Ad account permission error'),
          text: this.tr('This ad account cannot be used because you do not have the required "Manage" and "Advertise" permissions on it.'),
          whatToDo: this.tr('Please contact the ad account administrator to grant you the necessary permissions, or visit Meta Business Suite to request access:')
            + '<br /><a href="' + metaLink + '" target="_blank">' + metaLink + '</a>',
          retry: {
            label: this.tr('I have the required permissions, try again'),
            errorMessage: this.tr('You still do not have the required permissions. Please contact your ad account administrator.'),
            isStillFailing: updated => updated.status === AD_ACCOUNT_STATUS.PERMISSION_ERROR,
          },
        };
      }

      if (this.adAccountStatusIsWithoutPaymentMethod(adAccount)) {
        const metaLink = 'https://business.facebook.com/billing_hub/payment_settings?asset_id=' + facebookAdAccountExternalId(adAccount);
        return {
          type: 'error',
          label: this.tr('Missing payment method'),
          text: this.tr('This ad account has no payment method set up at Meta and cannot be used until a valid payment method is added.'),
          whatToDo: this.tr('You can set up a payment method at the following link:')
            + '<br /><a href="' + metaLink + '" target="_blank">' + metaLink + '</a>',
          retry: {
            label: this.tr('Set, let\'s try again'),
            errorMessage: this.tr('The advertising account still does not have a valid payment method. Please try again.'),
            isStillFailing: updated => updated.has_payment_method !== true,
          },
        };
      }

      if (adAccount.status != null && adAccount.status !== AD_ACCOUNT_STATUS.ACTIVE) {
        const label = this.adAccountStatusLabel(adAccount);

        if (label) {
          return {
            type: 'error',
            label,
            text: label,
          };
        }
      }

      if (!adAccount.tos_accepted) {
        const externalId = adAccount.external_id.replace('act_', '');
        const tosUrl = `https://www.facebook.com/customaudiences/app/tos/?act=${externalId}`;

        let smallText = this.tr('Once you\'ve accepted the terms, it may take 1-2 hours for us to receive the updated information.');
        if (isAiMode) {
          smallText = this.tr('Once you\'ve accepted the terms, it may take 1-2 hours for us to receive the updated information, but you can still launch campaigns in the meantime.');
        }

        return {
          type: 'warning',
          label: this.tr('Action Required'),
          text: this.tr('The Custom Audience terms of service have not been accepted for this ad account. This may cause issues with your campaigns.'),
          whatToDo: this.tr('Please accept the Custom Audience terms of service for the selected ad account before proceeding.') + '<br/>'
            + '<a href="' + tosUrl + '" target="_blank">' + this.tr('Accept terms of services') + '</a>'
            + '<small>' + smallText + '</small>',
        };
      }

      return null;
    },

    sourceProfileStatusText(sourceProfile) {
      if (!sourceProfile) {
        return null;
      }

      let sourceProfileType = this.tr('Source');
      switch (sourceProfile.platform) {
        case 'facebook':
          sourceProfileType = this.tr('Facebook page');
          break;
        case 'instagram':
          sourceProfileType = this.tr('Instagram account');
          break;
      }

      let profileType = this.tr('Profile');
      switch (sourceProfile.platform) {
        case 'facebook':
        case 'instagram':
          profileType = this.tr('Facebook Profile');
          break;
      }

      if (sourceProfile.deleted_at) {
        return {
          type: 'error',
          label: this.tr('Source disconnected'),
          text: this.tr(
            'The ":sourceProfileName" :sourceProfileType associated with the brand has been disconnected from :app.',
            {
              ':sourceProfileName': sourceProfile.name,
              ':sourceProfileType': sourceProfileType,
            },
          ),
          whatToDo: this.tr(
            'Reconnect the profile that links the :sourceProfileType to :app.',
            {
              ':sourceProfileType': sourceProfileType,
            },
          ),
        };
      }

      if (sourceProfile.lost_access) {
        return {
          type: 'error',
          label: this.tr('Lost access to source'),
          text: this.tr(
            'Access to the ":sourceProfileName" :sourceProfileType connected by the :profileType has been terminated.',
            {
              ':sourceProfileName': sourceProfile.name,
              ':sourceProfileType': sourceProfileType,
              ':profileType': profileType,
            },
          ),
          whatToDo: this.tr(
            'Please ask the authorized person to restore access or reconnect the data source through a :profileType that still has access.',
            {
              ':profileType': profileType,
            },
          ),
        };
      }

      return null;
    },
  },
};

function integrationsPathForPlatform(platform) {
  switch (platform) {
    case 'facebook':
      return '/integrations/meta';
  }

  if (platform) {
    // eslint-disable-next-line no-console
    console.error(`AssetStatus: no integrations page is mapped for the "${platform}" platform.`);
  }

  return null;
}

function blockedRouteAction(component, reason) {
  switch (reason) {
    case 'expired':
      return component.tr('the connection has expired, the profile has to be reconnected');
    case 'missing_scope':
      return component.tr('a permission is missing, the profile has to be reconnected with every requested permission granted');
    case 'checkpoint':
      return component.tr('Facebook asks for a security check, which has to be completed on Facebook');
  }

  return component.tr('the profile cannot be used right now');
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function connectedProfileTypeLabel(component, connectedProfile) {
  switch (connectedProfile?.platform) {
    case 'facebook':
      return component.tr('Facebook Profile');
  }

  return component.tr('Profile');
}

function facebookAdAccountExternalId(adAccount) {
  const raw = adAccount?.external_id || adAccount?.id;
  if (!raw) {
    return '';
  }
  return String(raw).replace('act_', '');
}
